import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthService } from "../Context/AuthContext";
import VoiceCloningSettings from "../Components/VoiceCloning/VoiceCloningSettings";
import { useTranslate } from "@tolgee/react";
import useLanguagePreFill from "../hooks/useLanguagePreFill";
import useMultiStepForm from "../hooks/useMultiStepForm";
import useBeforeUnloadWarning from "../hooks/useBeforeUnloadWarning";
import { Container, Content, Card, Header, Title, Subtitle } from "../Components/Auth Components/shared/OnboardingStyles";
import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import ProgressDots from "../Components/GenericComponents/ProgressDots";
import CulturalBgInfoModal from "../Components/Auth Components/CulturalBgInfoModal";
import InfoModal from "../Components/Settings/InfoModal";
import UserProfileForm from "../Components/Auth Components/UserProfileForm";
import OnboardingReadyScreen from "../Components/Auth Components/OnboardingReadyScreen";
import { useLinksquaredService } from "../Context/LinksquaredContext";
import LocalStorage from "../Helpers/LocalStorage";
import { useNotify } from "../Helpers/Notifications";
import { buildUserProfile } from "../Helpers/buildUserProfile";


const OnboardingPage = () => {
  const authMethod = LocalStorage.getAuthMethod();

  const {
    translationsLanguages,
    getTranslationsLanguages,
    getCallTranslationsLanguages,
    user,
    updateUser,
    registerUserWiwthSSO,
  } = useAuthService();
  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const { step, next, back } = useMultiStepForm(3);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("");
  const [culturalBg, setCulturalBg] = useState("");
  const [gender, setGender] = useState(null);
  const [birthday, setBirthday] = useState(null);
  const [isCulturalBgModalOpen, setIsCulturalBgModalOpen] = useState(false);
  const [isDetailsInfoModalOpen, setIsDetailsInfoModalOpen] = useState(false);
  const { getInvitedBy } = useLinksquaredService();
  const navigate = useNavigate();
  const { notifyError } = useNotify();

  const canContinue = name.trim().length >= 3 && language !== "";

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  useEffect(() => {
    getTranslationsLanguages(() => {}, () => {});
    getCallTranslationsLanguages(() => {}, () => {});
  }, []);

  useLanguagePreFill(language, setLanguage, translationsLanguages);
  useBeforeUnloadWarning(step !== 3);

  const handleContinue = () => {
    if (!canContinue) return;
    setIsLoading(true);

    const objectData = buildUserProfile({
      name, language, culturalBg, gender, birthday,
      countryCode: user?.country_code,
      invitedBy: getInvitedBy(),
    });

    const handleSuccess = () => {
      setIsLoading(false);
      next();
    };

    const handleError = () => {
      setIsLoading(false);
      notifyError(`${t("general.something_wrong")}`);
    };

    if (authMethod !== "email") {
      registerUserWiwthSSO(objectData, handleSuccess, handleError);
    } else {
      updateUser(objectData, handleSuccess, handleError);
    }
  };

  return (
    <SpinnerComponent isLoading={isLoading}>
      {isCulturalBgModalOpen && (
        <CulturalBgInfoModal openModal={() => setIsCulturalBgModalOpen(false)} />
      )}
      {isDetailsInfoModalOpen && (
        <InfoModal infoType="details" openModal={() => setIsDetailsInfoModalOpen(false)} />
      )}

      <Container>
        <Content>
          <Card>
            {step !== 3 && (
              <ProgressDots
                step={step}
                total={2}
                onBack={step === 2 ? back : null}
              />
            )}

            {step === 1 && (
              <>
                <Header>
                  <Title>{t("authentication.onboarding.header_title")}</Title>
                  <Subtitle>{t("authentication.onboarding.header_subtitle")}</Subtitle>
                </Header>
                <UserProfileForm
                  name={name} setName={setName}
                  language={language} setLanguage={setLanguage}
                  translationsLanguages={translationsLanguages}
                  birthday={birthday} setBirthday={setBirthday}
                  gender={gender} setGender={setGender}
                  culturalBg={culturalBg} setCulturalBg={setCulturalBg}
                  onInfoClick={() => setIsCulturalBgModalOpen(true)}
                  onDetailsInfoClick={() => setIsDetailsInfoModalOpen(true)}
                  canContinue={canContinue}
                  onContinue={handleContinue}
                />
              </>
            )}

            {step === 2 && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Title style={{ textAlign: "center" }}>{t("voice_cloning.onboarding_title")}</Title>
                <VoiceCloningSettings
                  locale={language}
                  onProceed={next}
                  onSetupLater={next}
                  showHeader={false}
                />
              </div>
            )}

            {step === 3 && (
              <OnboardingReadyScreen
                name={user?.name || name}
                onChat={() => { LocalStorage.setSidebar("chats"); navigate("/"); }}
                onCall={() => { LocalStorage.setSidebar("calls"); navigate("/"); }}
              />
            )}
          </Card>
        </Content>
      </Container>
    </SpinnerComponent>
  );
};

export default OnboardingPage;
